/**
 * Effect-only hook that mirrors fetched submission, auth-on-file, clinical and
 * order-id values into the {@link useInitialDataOfSingleMedicalOrder} context
 * so downstream consumers (e.g. write-back UI) can read a stable snapshot.
 *
 * Extracted verbatim from {@link SingleOrderLayout}.
 *
 * @module useSingleOrderInitialDataSync
 */
import { useEffect } from "react";
import { useInitialDataOfSingleMedicalOrder } from "../../../context/initialDataOfSingleMedicalOrder";
import {
  AuthOnFile,
  ClinicalDataEntry,
} from "../../../data-model/medicalPaOrdersModel";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";

interface UseSingleOrderInitialDataSyncArgs {
  submission: Submission | null;
  authOnFile: AuthOnFile | null;
  /**
   * Must be `null` (not `[]`) when no data has loaded — see the module doc on
   * {@link useSingleOrderUnwrappedData}. Substituting `[]` in the caller would
   * produce a fresh array reference per render and cause an infinite loop here
   * because `clinicalEntries` is in the effect's dep array.
   */
  clinicalEntries: ClinicalDataEntry[] | null;
  orderId: string | undefined;
}

/**
 * Pushes the latest fetched values into the initial-data context whenever
 * any of the inputs change. The `clinicalEntries ?? []` substitution happens
 * INSIDE the effect (not at the call site) so the dep array sees a stable
 * `null` reference between renders that have no data yet.
 */
export const useSingleOrderInitialDataSync = ({
  submission,
  authOnFile,
  clinicalEntries,
  orderId,
}: UseSingleOrderInitialDataSyncArgs): void => {
  const {
    setSubmissionData,
    setAuthOnFileDetails,
    setOrderId,
    setClinicalData,
  } = useInitialDataOfSingleMedicalOrder();

  useEffect(() => {
    if (submission) {
      setSubmissionData(submission);
    }
    if (authOnFile) {
      setAuthOnFileDetails(authOnFile);
    }
    setOrderId(orderId ?? "");
    setClinicalData(clinicalEntries ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOnFile, clinicalEntries, orderId, submission]);
};
